import { memo, useState } from 'react';
import { isEqual, uniq } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, IconButton, Stack, Tooltip, tooltipClasses } from '@mui/material';
import { AddRounded as AddIcon } from '@mui/icons-material';
import { useEditorStore, useAssistantLoadingStore } from '@diana/core';
import { TaskBuilder } from '../TaskBuilder/TaskBuilder';
import { IStepBuilderProps } from './StepBuilder.types';
import { FLOW_BUILDER2_PAGE_NS } from '../../../i18n';
import { renameTask } from '../Support';
import { useAddTask } from '../../../Support/useAddTask';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';

export const StepBuilder = memo((props: IStepBuilderProps) => {
  const { stepId, flowId } = props;

  const taskIds = useEditorStore((s) => {
    const steps = s.flow?.steps.filter((step) => step.stepId === stepId);
    return uniq(steps?.map((step) => step.taskId) ?? []);
  }, isEqual);

  return (
    <Stack id={stepId} data-step-id={stepId} gap={2}>
      {taskIds.map((taskId) => (
        <Box key={taskId}>
          <TaskBuilder taskId={taskId} stepId={stepId} flowId={flowId} />
        </Box>
      ))}
      <AddTaskDivider {...props} />
    </Stack>
  );
}, isEqual);

const AddTaskDivider = (props: IStepBuilderProps) => {
  const { stepId } = props;

  const [isHovered, setIsHovered] = useState<boolean>(false);

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);
  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const disabled = hasAnyPendingStatus || isChatLoading;

  const { addTask, isAddingTask } = useAddTask({
    onSuccess: (steps) => {
      const step = [...steps].reverse().find((step) => step.stepId === stepId);
      if (step)
        setTimeout(() => {
          renameTask(step.taskId);
        });
    },
  });

  return (
    <Stack
      direction="row"
      alignItems="center"
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) {
          addTask(stepId).catch();
        }
      }}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={({ palette }) => ({
        cursor: disabled ? 'auto' : 'pointer',
        ...(!isAddingTask && !disabled
          ? {
              '&:hover .MuiIconButton-root': {
                p: 0.25,
                color: palette.primary.contrastText,
                backgroundColor: palette.primary.main,
                borderWidth: 0,
              },
            }
          : {}),
      })}
    >
      <Box sx={({ palette }) => ({ flex: 1, borderBottom: `1px dashed ${palette.border.main}` })} />
      <Tooltip
        open={isHovered && !isAddingTask && !disabled}
        placement="right"
        title={t('buttons.addTask')}
        slotProps={{
          popper: { sx: { [`.${tooltipClasses.tooltip}`]: { marginLeft: '4px !important' } } },
        }}
      >
        <IconButton
          size="small"
          disabled={isAddingTask || disabled}
          sx={({ palette }) => ({
            p: 0.125,
            border: `1px solid ${palette.divider}`,
            backgroundColor: palette.background.card,
          })}
        >
          {isAddingTask ? (
            <CircularProgress size="1em" />
          ) : (
            <AddIcon fontSize="small" color="inherit" />
          )}
        </IconButton>
      </Tooltip>
      <Box sx={({ palette }) => ({ flex: 1, borderBottom: `1px dashed ${palette.border.main}` })} />
    </Stack>
  );
};
